// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDebounce } from '@node-space/hooks'
import {
  Box,
  Button,
  Box as ContactInfoContainer,
  Box as Container,
  EmptyState,
  Icon,
  Input,
  Pagination,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { PageHeader } from 'components/header/PageHeader'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useContactInstrumentQuery } from 'hooks/queries/useContactInstrumentsQuery'
import { useContactQuery } from 'hooks/queries/useContactQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import {
  ContactActionIds,
  ContactInstrumentActionId,
  ContactInstrumentActionIds,
} from 'types/atoms/atom.contacts'
import { InstrumentType } from 'types/contacts'
import { contactActionAtom, contactAtom, contactInstrumentActionAtom } from './atoms/contactAtoms'
import { AddAccountSheet } from './components/AddAccountSheet'
import { ContactInfo } from './components/ContactInfo'
import { ContactsSheets } from './components/ContactsSheets'
import { ContactInstrumentsSheet } from './ContactInstruments'
import { ContactInstrumentsList } from './ContactInstrumentsList'
import { ContactFormKeys } from './schemas/contactSchema'
import { getRequiredContactFieldsForCryptoAddress } from './utils/fiatInstruments'

export const Contact = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { enableAddAccountSheet } = useFeatureFlags()
  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const { id }: { id?: number } = useParams()

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchTerm = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = useSearchablePagination(
    10,
    debouncedSearchTerm
  )

  const { data: contact, isError: isContactError } = useContactQuery(id?.toString())
  const {
    data: { content: instrumentsList, pagination } = { pagination: { total: 0 } },
    isFetching,
    isError,
  } = useContactInstrumentQuery({
    contactId: id?.toString(),
    size: rowsPerPage,
    page,
    searchQuery: debouncedSearchTerm,
  })

  const paginationData = useMemo(() => {
    return pagination?.total
      ? {
          total: pagination.total,
          page: pagination.page || 0,
          offset: pagination.offset || 0,
          max: pagination.max || rowsPerPage,
        }
      : undefined
  }, [pagination])

  const { showPagination } = usePaginationPagingPersist(paginationData)

  const [contactAction, setContactAction] = useAtom(contactActionAtom)
  const [isContactInstrumentSheetOpen, setIsContactInstrumentSheetOpen] = useState(false)
  const [isAddAccountSheetOpen, setIsAddAccountSheetOpen] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)
  const setContact = useSetAtom(contactAtom)

  useEffect(() => {
    if (!isEmpty(contact)) {
      setContact(contact)
    }
  }, [contact])

  const triggerOpenSheet = ({
    actionId,
    header,
  }: {
    actionId: ContactInstrumentActionId
    header: string
  }) => {
    setContact(contact)
    setContactInstrumentAction({
      ...contactInstrumentAction,
      actionId: actionId,
      header: header,
      onClose: () => {
        setIsContactInstrumentSheetOpen(false)
        setContactInstrumentAction(null)
      },
    })
    setIsContactInstrumentSheetOpen(true)
  }

  const handleEditContactForCryptoAddress = useCallback(
    (requiredFields: ContactFormKeys[]) => {
      if (requiredFields.length) {
        setContactAction({
          ...contactAction,
          actionId: ContactActionIds.CONTACT,
          header: t('contacts.updateContact'),
          onClose: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] })
            queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACT, id?.toString()] })
            setIsContactSheetOpen(false)
            contactAction?.onClose?.()
          },
          meta: {
            keepUpdatedContact: true,
            flowType: InstrumentType.CRYPTO,
            contactRequiredFields: {
              requiredFields,
              calloutMessage: t('contacts.cryptoAccountRequiredAdditionalContactDetails'),
            },
          },
        })
        setIsContactSheetOpen(true)
      }
    },
    [setContactAction]
  )

  const openCreateCryptoAccountSheet = () => {
    const requiredFields = getRequiredContactFieldsForCryptoAddress(contact)

    if (requiredFields?.length) {
      handleEditContactForCryptoAddress(requiredFields)
      return
    }

    triggerOpenSheet({
      actionId: ContactInstrumentActionIds?.INSTRUMENT,
      header: t('contacts.addCryptoAccount'),
    })
  }

  const openCreateBankAccountSheet = () => {
    triggerOpenSheet({
      actionId: ContactInstrumentActionIds?.FIAT_INSTRUMENT,
      header: t('contacts.addBankAccount'),
    })
  }

  const openAddAccountSheet = () => {
    setContact(contact)
    setContactInstrumentAction({
      ...contactInstrumentAction,
      onClose: () => {
        setIsAddAccountSheetOpen(false)
        setContactInstrumentAction(null)
      },
    })
    setIsAddAccountSheetOpen(true)
  }

  const handleAddAccountSheetCryptoSelected = useCallback(() => {
    const requiredFields = getRequiredContactFieldsForCryptoAddress(contact)
    if (requiredFields?.length) {
      setIsAddAccountSheetOpen(false)
      setContactInstrumentAction(null)
      handleEditContactForCryptoAddress(requiredFields)
    }
  }, [contact, handleEditContactForCryptoAddress])

  const onEditClick = () => {
    setContact(contact)
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT,
      header: t('contacts.updateContact'),
      onClose: () => {
        queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACT, id?.toString()] })
        setIsContactSheetOpen(false)
      },
      meta: {
        keepUpdatedContact: true,
      },
    })
    setIsContactSheetOpen(true)
  }

  return (
    <>
      <PageHeader
        description={t('contacts.contactDetailsDescription', { contactName: contact?.name })}
        renderRightSection={
          canEdit &&
          !isFetching &&
          !!instrumentsList?.length && (
            <Box flex gapX={16}>
              {!viewOnly && enableAddAccountSheet ? (
                <Button
                  testId="add-contact-account"
                  variant="secondary"
                  disabled={viewOnly}
                  onClick={openAddAccountSheet}
                  iconElement={<Icon name="Plus" />}
                >
                  {t('contacts.addAccount')}
                </Button>
              ) : (
                <>
                  <Button
                    testId="add-contact-instrument"
                    variant="secondary"
                    disabled={viewOnly}
                    onClick={openCreateCryptoAccountSheet}
                    iconElement={<Icon name="Plus" />}
                  >
                    {t('contacts.addCryptoAddress')}
                  </Button>
                  <Button
                    testId="add-contact-bank-account-instrument"
                    variant="secondary"
                    disabled={viewOnly}
                    onClick={openCreateBankAccountSheet}
                    iconElement={<Icon name="Plus" />}
                  >
                    {t('contacts.bankAccount')}
                  </Button>
                </>
              )}
            </Box>
          )
        }
      />

      <Container flexCol className="md:flex-row" gapX={16} gapY={16} width="full">
        <Box
          flexCol
          width="full"
          className={clsx({
            'break-words order-2 md:order-none': !isError && !isFetching,
          })}
        >
          {/* Show error state */}
          {isError && !isFetching && (
            <EmptyState
              testId="error-state"
              LottiePlayer={<Lottie src="connection-error" />}
              title={t('defaultError')}
              description={
                <>
                  {`${t('requestErrors.comeBackLater')} `}
                  <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                    {t('requestErrors.refreshPage')}
                  </Button>
                </>
              }
            />
          )}

          {/* Show content state */}
          {!isError && (
            <>
              <Box width="full" paddingB={24}>
                <Input
                  placeholderText={t('contacts.searchInstrumentsPlaceholder')}
                  onChange={event => setSearchQuery(event.target.value)}
                  value={(searchQuery || '')?.trim()}
                  searchable
                  IconButton={
                    searchQuery && (
                      <Button
                        variant="ghost"
                        onClick={() => setSearchQuery('')}
                        iconElement={<Icon name="X" />}
                      />
                    )
                  }
                  iconPosition="right"
                  testId="contact-instruments-search-field"
                />
              </Box>
              {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

              {debouncedSearchTerm && !instrumentsList?.length && !isFetching && (
                <NoSearchResults clearSearchCriteria={() => setSearchQuery('')} />
              )}

              {!!instrumentsList?.length && !isFetching && (
                <>
                  <ContactInstrumentsList
                    contactInstruments={instrumentsList}
                    setIsContactInstrumentSheetOpen={setIsContactInstrumentSheetOpen}
                  />

                  {!!pagination && !showPagination && (
                    <Container className={`${isFetching ? 'opacity-25' : ''}`}>
                      <Pagination
                        testId="contact-instruments-pagination"
                        page={page}
                        rowsPerPage={rowsPerPage}
                        total={pagination?.total || 0}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                      />
                    </Container>
                  )}
                </>
              )}
            </>
          )}

          {/* Show empty state */}
          {!isFetching && !isError && !instrumentsList?.length && !debouncedSearchTerm && (
            <EmptyState
              testId="empty-state"
              LottiePlayer={<Lottie src="no-data" />}
              title={t('contacts.noContactInstrumentHeadline', {
                contactName: contact?.name,
              })}
              description={t('contacts.addContactInstrumentDescription')}
              PrimaryButton={
                <Button
                  iconElement={<Icon name="Plus" />}
                  data-testId="add-contact-instrument"
                  disabled={viewOnly || !canEdit}
                  onClick={
                    enableAddAccountSheet ? openAddAccountSheet : openCreateCryptoAccountSheet
                  }
                >
                  {enableAddAccountSheet ? t('contacts.addAccount') : t('contacts.cryptoAccount')}
                </Button>
              }
              SecondaryButton={
                !enableAddAccountSheet && (
                  <Button
                    testId="add-contact-bank-account-instrument"
                    disabled={viewOnly || !canEdit}
                    onClick={openCreateBankAccountSheet}
                    iconElement={<Icon name="Plus" />}
                  >
                    {t('contacts.bankAccount')}
                  </Button>
                )
              }
            />
          )}
        </Box>

        {!isEmpty(contact) && !isContactError && (
          <ContactInfoContainer
            flexCol
            className="max-w-[320px] min-w-[260px] w-full order-1 md:order-none"
          >
            <ContactInfo onEditClick={onEditClick} />
          </ContactInfoContainer>
        )}
      </Container>
      <ContactsSheets isSheetOpen={isContactSheetOpen} />
      <ContactInstrumentsSheet isSheetOpen={isContactInstrumentSheetOpen} />
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
      <AddAccountSheet
        isSheetOpen={isAddAccountSheetOpen}
        onCryptoTabSelected={handleAddAccountSheetCryptoSelected}
      />
    </>
  )
}
